import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { searchCollectibles } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Menu, X, LogOut, Package, LayoutDashboard, Heart, ShoppingBag, User, Sparkles, Search, Loader2, Brain } from 'lucide-react';

function ChestIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 11v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8" />
      <path d="M3 11V9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" />
      <path d="M3 11h18" />
      <path d="M12 7v4" />
      <rect x="9" y="9" width="6" height="4" rx="1" />
      <path d="M7 7V5a5 5 0 0 1 10 0v2" />
    </svg>
  );
}

export default function Navbar() {
  const { user, logoutUser } = useAuth();
  const { cart, refreshCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) refreshCart();
  }, [user, refreshCart]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    setSearching(true);
    try {
      const { data } = await searchCollectibles(q);
      setSearchResults(data.results || []);
      setSearchOpen(true);
    } catch { setSearchResults([]); }
    setSearching(false);
  }, []);

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.length >= 2) {
      setSearchOpen(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
    if (e.key === 'Escape') { setSearchOpen(false); }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Gold', to: '/gold' },
    { label: 'Silver', to: '/silver' },
    { label: 'Custom', to: '/custom' },
    { label: 'Live Rate', to: '/rates' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background/85 backdrop-blur-xl border-b border-white/5'
            : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <span className="font-display text-xl md:text-2xl font-bold tracking-tight">
                S<span className="text-gold">Ō</span>HM
              </span>
            </Link>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} data-testid={`nav-${link.label.toLowerCase()}`}>
                  <span
                    className={`text-sm font-medium px-4 py-2 rounded-full transition-all duration-300 ${
                      isActive(link.to)
                        ? 'bg-gold/15 text-gold'
                        : 'text-foreground/50 hover:text-foreground'
                    }`}
                  >
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>

            {/* Right */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="hidden md:flex relative" ref={searchRef}>
                <div className="flex items-center bg-secondary/60 rounded-full px-3.5 h-9 gap-2 w-48 lg:w-56" data-testid="search-bar">
                  {searching ? (
                    <Loader2 className="w-3.5 h-3.5 text-gold flex-shrink-0 animate-spin" />
                  ) : (
                    <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                  <input
                    type="text"
                    placeholder="find your loot"
                    value={searchQuery}
                    onChange={onSearchChange}
                    onKeyDown={onSearchKeyDown}
                    onFocus={() => { if (searchResults.length > 0) setSearchOpen(true); }}
                    className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 outline-none w-full font-mono"
                    data-testid="search-input"
                  />
                </div>
                {searchOpen && searchResults.length > 0 && (
                  <div className="absolute top-11 right-0 w-80 bg-background/95 backdrop-blur-xl border border-border/30 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="search-results-dropdown">
                    <div className="px-3 py-2 border-b border-border/20">
                      <p className="text-xs text-muted-foreground">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {searchResults.slice(0, 6).map((item) => (
                        <Link
                          key={item.collectible_id}
                          to={`/collectible/${item.collectible_id}`}
                          onClick={() => setSearchOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/50 transition-colors"
                          data-testid={`search-result-${item.collectible_id}`}
                        >
                          <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover bg-secondary" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{item.metal_type} &middot; {item.weight}g &middot; {item.purity}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {searchResults.length > 6 && (
                      <Link
                        to={`/search?q=${encodeURIComponent(searchQuery)}`}
                        onClick={() => setSearchOpen(false)}
                        className="block text-center py-2.5 text-xs text-gold hover:bg-secondary/30 border-t border-border/20"
                        data-testid="search-view-all"
                      >
                        View all {searchResults.length} results
                      </Link>
                    )}
                  </div>
                )}
                {searchOpen && searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                  <div className="absolute top-11 right-0 w-80 bg-background/95 backdrop-blur-xl border border-border/30 rounded-xl shadow-2xl p-4 z-50" data-testid="search-no-results">
                    <p className="text-sm text-muted-foreground text-center">No loot found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
              {user ? (
                <>
                  <Link to="/chest" data-testid="nav-chest">
                    <Button variant="ghost" className="relative rounded-full w-10 h-10 p-0 hover:bg-secondary">
                      <ShoppingBag className="w-5 h-5" />
                      {cart.total_items > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-gold text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">
                          {cart.total_items}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="rounded-full w-10 h-10 p-0 hover:bg-secondary" data-testid="user-menu-trigger">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                            <span className="text-foreground/70 text-sm font-semibold">{user.name?.[0]?.toUpperCase()}</span>
                          </div>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 rounded-xl shadow-lg border-border/40">
                      <div className="px-3 py-2.5">
                        <p className="text-sm font-semibold truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                        <Link to="/portfolio" className="flex items-center gap-2.5" data-testid="menu-portfolio">
                          <User className="w-4 h-4" /> My Vault
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                        <Link to="/chest" className="flex items-center gap-2.5" data-testid="menu-chest">
                          <ShoppingBag className="w-4 h-4" /> My Chest
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                        <Link to="/orders" className="flex items-center gap-2.5" data-testid="menu-orders">
                          <Package className="w-4 h-4" /> Orders
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                        <Link to="/wishlist" className="flex items-center gap-2.5" data-testid="menu-wishlist">
                          <Heart className="w-4 h-4" /> Wishlist
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                        <Link to="/custom" className="flex items-center gap-2.5" data-testid="menu-custom">
                          <Sparkles className="w-4 h-4" /> Custom Jewelry
                        </Link>
                      </DropdownMenuItem>
                      {user.is_admin && (
                        <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                          <Link to="/admin" className="flex items-center gap-2.5" data-testid="menu-admin">
                            <LayoutDashboard className="w-4 h-4" /> Admin
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {user.is_admin && (
                        <DropdownMenuItem asChild className="cursor-pointer rounded-lg">
                          <Link to="/pothdar" className="flex items-center gap-2.5" data-testid="menu-pothdar">
                            <Brain className="w-4 h-4" /> Pothdar AI
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logoutUser} className="cursor-pointer text-destructive rounded-lg" data-testid="menu-logout">
                        <LogOut className="w-4 h-4 mr-2.5" /> Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="hidden md:flex text-sm font-medium rounded-full px-5 h-9 text-foreground/60 hover:text-foreground hover:bg-white/5" data-testid="nav-login-button">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/login?signup=true">
                    <Button className="text-sm font-medium rounded-full px-5 h-9 bg-gold text-background hover:bg-gold/90" data-testid="nav-signup-button">
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}

              <Button
                variant="ghost"
                className="md:hidden rounded-full w-10 h-10 p-0 hover:bg-secondary"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border/20 pb-6" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-5 pt-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                    isActive(link.to) ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {user && (
                <>
                  <Link to="/chest" className="flex items-center gap-3 px-4 py-3 text-base font-medium rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/50">
                    <ShoppingBag className="w-5 h-5" /> Chest {cart.total_items > 0 && <span className="ml-auto text-xs bg-gold text-white px-2 py-0.5 rounded-full">{cart.total_items}</span>}
                  </Link>
                  <Link to="/portfolio" className="flex items-center gap-3 px-4 py-3 text-base font-medium rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/50">
                    <User className="w-5 h-5" /> My Vault
                  </Link>
                </>
              )}
              {!user && (
                <div className="pt-4 px-4">
                  <Link to="/login">
                    <Button className="w-full rounded-full h-11 text-sm font-semibold">Sign In</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
      {/* Spacer */}
      <div className="h-16 md:h-20" />
    </>
  );
}
